import {
  type AdminOrderDestination,
  findShippingRegion,
  SHIPPING_REGION_LABELS,
  type ShippingAddress,
  type UpdateOrderAddressInput,
  updateOrderAddressInputSchema,
} from '@repo/api-contracts';
import { useId, useState } from 'react';
import { formatCount } from '@/shared/lib';
import { formStyles, Input, Label, OrderActionDialog } from '@/shared/ui';

interface OrderAddressDialogProps {
  readonly orderId: string;
  /** 바꿀 배송지 — 그 배송지 주소로 시작하고 그 배송지 id 로 저장한다. */
  readonly destination: AdminOrderDestination;
  readonly onSubmit: (input: UpdateOrderAddressInput) => Promise<unknown>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
}

const FIELDS = [
  ['recipientName', '수령인'],
  ['recipientPhone', '수령인 연락처'],
  ['postalCode', '우편번호'],
  ['address1', '주소'],
  ['address2', '상세주소'],
] as const;

/**
 * 배송지별 주소 변경(SUPER-86 §5-4·D11) — 계약 `updateOrderAddressInputSchema`(우편번호 5자리 포함)로 검증. 관리자는 지역 구분이
 * 바뀌어도 저장할 수 있고(서버도 막지 않는다), 배송비 차액은 운영자가 따로 처리한다는 안내만 보인다. 우편번호가 5자리 숫자가 아니면
 * 지역을 구하지 않고(안내 없음) 검증에서 막는다. 지역을 모르는 옛 배송지(region null)는 비교하지 않는다. 상태 충돌이면 고친 입력을
 * 그대로 둔다. 우편번호 아래에 지금 입력에서 구한 지역(일반/제주/도서산간, 모르면 '-')을 보인다. 관리자 화면에는 주소 검색이 없어
 * 직접 입력한다(web 의 검색 실패 대체 경로와 같다) — 지역이 바뀌면 위 안내가 뜬다.
 */
export function OrderAddressDialog({
  orderId,
  destination,
  onSubmit,
  onReload,
  onClose,
  pending,
}: OrderAddressDialogProps) {
  const id = useId();
  const [value, setValue] = useState<ShippingAddress>(destination.shippingAddress);
  const input = { orderId, destinationId: destination.id, shippingAddress: value };
  const currentRegion = destination.region;
  const nextRegion = findShippingRegion(value.postalCode.trim());
  const regionChanged =
    currentRegion !== null && nextRegion !== null && nextRegion !== currentRegion;
  return (
    <OrderActionDialog
      title="배송지 변경"
      submitLabel="배송지 저장"
      onReload={onReload}
      onClose={onClose}
      pending={pending}
      validate={() =>
        updateOrderAddressInputSchema.safeParse(input).success
          ? null
          : '배송지를 확인해 주세요. 빈 칸이 있거나 길이 제한을 넘었고, 우편번호는 숫자 5자리입니다.'
      }
      onSubmit={async () => {
        await onSubmit(updateOrderAddressInputSchema.parse(input));
      }}
    >
      <p className={formStyles.hint}>
        배송지 {destination.sortOrder + 1} · 수량 {formatCount(destination.quantity)}개
      </p>
      <p className={formStyles.hint}>우편번호와 주소를 직접 입력해 주세요.</p>
      {FIELDS.map(([key, label]) => (
        <div key={key} className={formStyles.field}>
          <Label htmlFor={`${id}-${key}`}>{label}</Label>
          <Input
            id={`${id}-${key}`}
            value={value[key]}
            onChange={(event) => setValue({ ...value, [key]: event.target.value })}
          />
          {key === 'postalCode' ? (
            <p className={formStyles.hint}>
              지역: {nextRegion === null ? '-' : SHIPPING_REGION_LABELS[nextRegion]}
            </p>
          ) : null}
        </div>
      ))}
      {regionChanged ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          지역 구분이 바뀝니다({SHIPPING_REGION_LABELS[currentRegion]} →{' '}
          {SHIPPING_REGION_LABELS[nextRegion]}). 배송비 차액은 결제 상세의 부분 환불 등으로 따로
          처리해 주세요.
        </p>
      ) : null}
    </OrderActionDialog>
  );
}
